import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  type AdminRefundSelection,
  type RefundableComponentOption,
  useCreateAdminRefundRequest,
  useGetAdminRefundOptions,
} from "@/api/pickup-refund-requests";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatCurrency } from "@/lib/format";

interface IssueRefundDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string;
  orderNumber: number;
  paidAmountCents: number;
  onSuccess?: () => void;
}

type Mode = "FULL" | "COMPONENTS" | "CUSTOM" | "GOODWILL";

const REASONS = [
  "Lost package",
  "Damaged goods",
  "Service issue",
  "Goodwill / customer satisfaction",
  "Other",
] as const;

function dollarsToCents(value: string): number | null {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return null;
  return Math.round(number * 100);
}

function componentLabel(component: RefundableComponentOption): string {
  const charge = component.chargeType.replace(/_/g, " ").toLowerCase();
  return `${charge.charAt(0).toUpperCase()}${charge.slice(1)}`;
}

function fundingLabel(component: RefundableComponentOption): string {
  return (
    component.fundingPayments
      .map(
        (payment) =>
          `${payment.method.replace(/_/g, " ")} · ${payment.referenceNumber ?? payment.id.slice(-8)}`
      )
      .join(", ") || "No funding payment"
  );
}

export function IssueRefundDialog({
  open,
  onOpenChange,
  orderId,
  orderNumber,
  paidAmountCents,
  onSuccess,
}: IssueRefundDialogProps) {
  const [mode, setMode] = useState<Mode>("FULL");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [customAmount, setCustomAmount] = useState("");
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const options = useGetAdminRefundOptions(orderId, open);
  const createRequest = useCreateAdminRefundRequest(orderId);
  const components = options.data ?? [];

  useEffect(() => {
    if (components.length === 0) return;
    setSelected((current) =>
      Object.keys(current).length > 0
        ? current
        : Object.fromEntries(
            components.map((component) => [component.key, true])
          )
    );
  }, [components]);

  const selectedComponents = useMemo(
    () => components.filter((component) => selected[component.key]),
    [components, selected]
  );
  const customCents = dollarsToCents(customAmount);
  const allocatedCents = components.reduce(
    (sum, component) =>
      sum + (dollarsToCents(allocations[component.key] ?? "") ?? 0),
    0
  );

  const reset = () => {
    setMode("FULL");
    setSelected({});
    setCustomAmount("");
    setAllocations({});
    setReason("");
    setNotes("");
  };

  const close = () => {
    if (createRequest.isPending) return;
    reset();
    onOpenChange(false);
  };

  const suggestAllocation = () => {
    if (!customCents) {
      toast.error("Enter the custom gross amount first");
      return;
    }
    let remaining = customCents;
    const next: Record<string, string> = {};
    for (const component of components) {
      const cents = Math.min(remaining, component.remainingGrossCents);
      if (cents > 0) next[component.key] = (cents / 100).toFixed(2);
      remaining -= cents;
    }
    setAllocations(next);
    if (remaining > 0) {
      toast.error("Custom amount exceeds component capacity", {
        description: `Classify the excess ${formatCurrency(remaining)} as EZX goodwill or reduce the amount.`,
      });
    }
  };

  const buildSelection = (): AdminRefundSelection | null => {
    if (mode === "FULL") return { mode: "FULL" };
    if (mode === "COMPONENTS") {
      if (selectedComponents.length === 0) {
        toast.error("Select at least one component");
        return null;
      }
      return {
        mode,
        components: selectedComponents.map(({ shipmentId, chargeType }) => ({
          shipmentId,
          chargeType,
        })),
      };
    }
    if (mode === "GOODWILL") {
      if (!customCents) {
        toast.error("Enter a valid goodwill amount");
        return null;
      }
      return { mode, amountGrossCents: customCents };
    }
    if (!customCents) {
      toast.error("Enter a valid custom gross amount");
      return null;
    }
    if (allocatedCents !== customCents) {
      toast.error("Component allocation must equal the custom gross amount", {
        description: `${formatCurrency(allocatedCents)} allocated of ${formatCurrency(customCents)}.`,
      });
      return null;
    }
    const requestedAllocations = components.flatMap((component) => {
      const amountGrossCents = dollarsToCents(allocations[component.key] ?? "");
      if (!amountGrossCents) return [];
      if (amountGrossCents > component.remainingGrossCents) {
        toast.error(`${componentLabel(component)} exceeds remaining capacity`);
        return [];
      }
      return [
        {
          shipmentId: component.shipmentId,
          chargeType: component.chargeType,
          amountGrossCents,
        },
      ];
    });
    if (
      requestedAllocations.reduce(
        (sum, allocation) => sum + allocation.amountGrossCents,
        0
      ) !== customCents
    ) {
      return null;
    }
    return {
      mode,
      amountGrossCents: customCents,
      allocations: requestedAllocations,
    };
  };

  const submit = async () => {
    if (!reason) {
      toast.error("Select a refund reason");
      return;
    }
    const selection = buildSelection();
    if (!selection) return;
    try {
      await createRequest.mutateAsync({
        reason: notes.trim() ? `${reason}: ${notes.trim()}` : reason,
        selection,
      });
      toast.success("Refund request created", {
        description:
          "No money moved yet. A different approver must review EZX-owned components; provider-owned components go to the carrier.",
      });
      close();
      onSuccess?.();
    } catch (error) {
      toast.error("Refund request could not be created", {
        description:
          error instanceof Error
            ? error.message
            : "Refresh component capacity and retry.",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create refund request</DialogTitle>
          <DialogDescription>
            ORD-{orderNumber} · Paid {formatCurrency(paidAmountCents)}. This
            creates a pending component authorization; it does not move money.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <RadioGroup
            value={mode}
            onValueChange={(value) => setMode(value as Mode)}
            className="grid gap-2 md:grid-cols-4"
          >
            {(
              [
                ["FULL", "Full eligible refund"],
                ["COMPONENTS", "Selected components"],
                ["CUSTOM", "Custom partial refund"],
                ["GOODWILL", "EZX goodwill"],
              ] as const
            ).map(([value, label]) => (
              <Label
                key={value}
                htmlFor={`refund-mode-${value}`}
                className="flex cursor-pointer items-center gap-2 rounded-md border p-3 font-normal"
              >
                <RadioGroupItem id={`refund-mode-${value}`} value={value} />
                {label}
              </Label>
            ))}
          </RadioGroup>

          {options.isLoading ? (
            <p role="status" className="text-sm text-muted-foreground">
              Loading refundable components…
            </p>
          ) : options.error ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Refundable components could not be loaded. Refresh before
                creating a request.
              </AlertDescription>
            </Alert>
          ) : mode === "GOODWILL" ? (
            <div className="space-y-2 rounded-md border p-4">
              <Label htmlFor="goodwill-amount">
                EZX-funded goodwill amount (gross CAD)
              </Label>
              <Input
                id="goodwill-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={customAmount}
                onChange={(event) => setCustomAmount(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Goodwill is a separate EZX-owned, non-tax component. Do not use
                it to hide an amount above service-component capacity.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {mode === "CUSTOM" && (
                <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
                  <div className="min-w-56 flex-1 space-y-1">
                    <Label htmlFor="custom-refund-amount">
                      Desired gross refund (CAD)
                    </Label>
                    <Input
                      id="custom-refund-amount"
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={customAmount}
                      onChange={(event) => setCustomAmount(event.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={suggestAllocation}
                  >
                    Suggest allocation
                  </Button>
                  <p className="w-full text-xs text-muted-foreground">
                    Allocated {formatCurrency(allocatedCents)} of{" "}
                    {formatCurrency(customCents ?? 0)}. Review and edit every
                    component allocation.
                  </p>
                </div>
              )}

              <div className="overflow-x-auto rounded-md border">
                <table className="w-full min-w-[980px] text-xs">
                  <thead className="bg-muted/50 text-left">
                    <tr>
                      <th className="p-2">Component</th>
                      <th className="p-2">Original net / tax / gross</th>
                      <th className="p-2">Refunded or pending</th>
                      <th className="p-2">Remaining net / tax / gross</th>
                      <th className="p-2">Owner</th>
                      <th className="p-2">Funding payment</th>
                      <th className="p-2">Requested gross / tax</th>
                    </tr>
                  </thead>
                  <tbody>
                    {components.map((component) => {
                      const requested =
                        mode === "FULL"
                          ? component.remainingGrossCents
                          : mode === "COMPONENTS"
                            ? selected[component.key]
                              ? component.remainingGrossCents
                              : 0
                            : (dollarsToCents(
                                allocations[component.key] ?? ""
                              ) ?? 0);
                      const requestedTax =
                        component.remainingGrossCents > 0
                          ? Math.round(
                              (component.remainingTaxCents * requested) /
                                component.remainingGrossCents
                            )
                          : 0;
                      return (
                        <tr key={component.key} className="border-t align-top">
                          <td className="p-2 font-medium">
                            {mode === "COMPONENTS" && (
                              <input
                                aria-label={`Select ${componentLabel(component)}`}
                                className="mr-2"
                                type="checkbox"
                                checked={Boolean(selected[component.key])}
                                onChange={(event) =>
                                  setSelected((current) => ({
                                    ...current,
                                    [component.key]: event.target.checked,
                                  }))
                                }
                              />
                            )}
                            {componentLabel(component)}
                            {component.shipmentId ? (
                              <span className="block font-mono text-[10px] text-muted-foreground">
                                {component.shipmentId}
                              </span>
                            ) : null}
                          </td>
                          <td className="p-2 font-mono">
                            {formatCurrency(component.capturedNetCents)} /{" "}
                            {formatCurrency(component.capturedTaxCents)} /{" "}
                            {formatCurrency(component.capturedGrossCents)}
                          </td>
                          <td className="p-2 font-mono">
                            {formatCurrency(component.reservedGrossCents)}
                          </td>
                          <td className="p-2 font-mono">
                            {formatCurrency(component.remainingNetCents)} /{" "}
                            {formatCurrency(component.remainingTaxCents)} /{" "}
                            {formatCurrency(component.remainingGrossCents)}
                          </td>
                          <td className="p-2">
                            {component.providerCarrier?.name ?? "EZX"}
                            <span className="block text-[10px] text-muted-foreground">
                              {component.approverType} approval
                            </span>
                          </td>
                          <td className="max-w-52 break-words p-2">
                            {fundingLabel(component)}
                          </td>
                          <td className="p-2">
                            {mode === "CUSTOM" ? (
                              <Input
                                aria-label={`${componentLabel(component)} allocation`}
                                className="h-8 w-28"
                                type="number"
                                min="0"
                                step="0.01"
                                max={component.remainingGrossCents / 100}
                                value={allocations[component.key] ?? ""}
                                onChange={(event) =>
                                  setAllocations((current) => ({
                                    ...current,
                                    [component.key]: event.target.value,
                                  }))
                                }
                              />
                            ) : (
                              <span className="font-mono">
                                {formatCurrency(requested)}
                              </span>
                            )}
                            <span className="mt-1 block text-[10px] text-muted-foreground">
                              tax {formatCurrency(requestedTax)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {components.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  No refundable component capacity remains for this order.
                </p>
              )}
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="refund-notes">Details (optional)</Label>
              <Textarea
                id="refund-notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Submitting creates a pending request. The requester cannot approve
              their own EZX-owned line without a recorded emergency override.
              Stripe/account-credit groups settle automatically after
              authorization; cash, e-transfer, and WeChat wait for payout
              confirmation.
            </AlertDescription>
          </Alert>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={close}
            disabled={createRequest.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={
              createRequest.isPending ||
              options.isLoading ||
              Boolean(options.error) ||
              (mode !== "GOODWILL" && components.length === 0)
            }
          >
            {createRequest.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Create pending request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
